import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { rememberPanelPosition, rememberedPanelPosition } from '@/lib/review-memory';
import { cn } from '@/lib/utils';

interface Position {
  x: number;
  y: number;
}

interface DraggablePanelProps {
  /** Its name in the browser's memory (lib/review-memory.ts). */
  storageKey: string;
  /** Classes placing the panel until it is first moved (e.g. `top-14 left-3`). */
  defaultClassName: string;
  className?: string;
  children: ReactNode;
}

const MARGIN = 8;

/**
 * A stage panel the dentist can move out of the way.
 *
 * It is dragged by any part marked `data-drag-handle` (its header), never by
 * the buttons, tabs or sliders inside that, and it stays inside the stage.
 * The spot is remembered; a double-click on the handle puts it back.
 */
export function DraggablePanel({
  storageKey,
  defaultClassName,
  className,
  children,
}: DraggablePanelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(() =>
    rememberedPanelPosition(storageKey),
  );
  const drag = useRef<{ pointer: number; dx: number; dy: number } | null>(null);

  const clamp = useCallback((next: Position): Position => {
    const panel = ref.current;
    const stage = panel?.offsetParent as HTMLElement | null;
    if (!panel || !stage) return next;
    const maxX = Math.max(MARGIN, stage.clientWidth - panel.offsetWidth - MARGIN);
    const maxY = Math.max(MARGIN, stage.clientHeight - panel.offsetHeight - MARGIN);
    return {
      x: Math.min(Math.max(MARGIN, next.x), maxX),
      y: Math.min(Math.max(MARGIN, next.y), maxY),
    };
  }, []);

  // A remembered spot from a bigger window, or a stage that shrank: keep it on screen.
  useEffect(() => {
    const stage = ref.current?.offsetParent as HTMLElement | null;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      setPosition((was) => (was ? clamp(was) : was));
    });
    observer.observe(stage);
    return () => {
      observer.disconnect();
    };
  }, [clamp]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (event.button !== 0 || !target.closest('[data-drag-handle]')) return;
    if (target.closest('button, input, select, textarea, a, [role="radio"]')) return;
    const panel = ref.current;
    if (!panel) return;
    event.preventDefault();
    panel.setPointerCapture(event.pointerId);
    drag.current = {
      pointer: event.pointerId,
      dx: event.clientX - panel.offsetLeft,
      dy: event.clientY - panel.offsetTop,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const moving = drag.current;
    if (!moving || moving.pointer !== event.pointerId) return;
    setPosition(clamp({ x: event.clientX - moving.dx, y: event.clientY - moving.dy }));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const moving = drag.current;
    if (!moving || moving.pointer !== event.pointerId) return;
    drag.current = null;
    ref.current?.releasePointerCapture(event.pointerId);
    setPosition((was) => {
      rememberPanelPosition(storageKey, was);
      return was;
    });
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    if (target.closest('button, input, select, textarea, a, [role="radio"]')) return;
    setPosition(null);
    rememberPanelPosition(storageKey, null);
  };

  return (
    <div
      ref={ref}
      className={cn('absolute', !position && defaultClassName, className)}
      style={position ? { left: position.x, top: position.y } : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
    >
      {children}
    </div>
  );
}
